import React from 'react';
import { Button } from '@mui/material'; // Import Button from MUI

interface DownloadCSVProps {
  data: { [key: string]: { [key: string]: any } }; // Define a more specific type for data if possible
}

const DownloadCSV: React.FC<DownloadCSVProps> = ({ data }) => {
  const downloadCSV = () => {
    // Check if data is valid and not empty
    if (!data || Object.keys(data).length === 0) {
      console.warn('No data provided to download.');
      return;
    }

    // Get all column keys from the data object (e.g., a, b, c...)
    const keys = Object.keys(data);

    // Ensure there's at least one key to determine rowCount
    if (keys.length === 0 || !data[keys[0]]) {
      console.warn('Invalid data structure for CSV download.');
      return;
    }

    // Assume all sub-objects have the same numeric keys (e.g., "0", "1", ...)
    const rowCount = Object.keys(data[keys[0]]).length;

    // Build rows dynamically
    const rows = [];
    for (let i = 0; i < rowCount; i += 1) {
      // Changed ++i to i += 1 to adhere to no-plusplus
      const row: { [key: string]: any } = {}; // Explicitly type row
      keys.forEach((key) => {
        row[key] = data[key][i.toString()];
      });
      rows.push(row);
    }

    // Create CSV header
    const csvHeader = `${keys.join(',')}\n`;

    // Create CSV rows
    const csvRows = rows
      .map((row) =>
        keys
          .map((key) => {
            // Ensure values are properly quoted if they contain commas or newlines
            const value = row[key];
            if (value === null || value === undefined) {
              return '';
            }
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
              return `"${stringValue.replace(/"/g, '""')}"`; // Escape double quotes
            }
            return stringValue;
          })
          .join(',')
      )
      .join('\n');

    const csvContent = csvHeader + csvRows;

    // Trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'dynamic_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up the object URL
  };

  return (
    <Button
      onClick={downloadCSV}
      variant="text" // Use 'text' variant to make it look like a link
      sx={{
        color: '#2563EB', // Equivalent to Tailwind text-blue-600
        fontWeight: 'fontWeightBold', // Equivalent to Tailwind font-bold
        fontSize: '0.875rem', // Equivalent to Tailwind text-sm (14px)
        textDecoration: 'underline', // Equivalent to Tailwind underline
        textUnderlineOffset: '4px', // Equivalent to Tailwind underline-offset-4
        textTransform: 'none', // Prevent MUI from uppercasing text by default
        '&:hover': {
          color: '#1E40AF', // Equivalent to Tailwind hover:text-blue-800
          textDecoration: 'underline', // Maintain underline on hover
          backgroundColor: 'transparent', // Ensure no background on hover if not desired
        },
        // Adjust padding and minWidth to make it behave more like a simple link button
        padding: 0,
        minWidth: 'unset',
      }}
    >
      Download
    </Button>
  );
};

export default DownloadCSV;
